// Shared role/permission gate used by row actions (ListView, DetailHeaderCard)
// and, via AddFormV1/EditFormV1, by field-level visiblePermission/editablePermission.
// A permission value looks like "<module>.<key>" (e.g. "submission.viewRate").
//
// checkPermission() is the CURRENT gate — it resolves against the `can`
// function from src/hooks/usePermissions.js (backed by GET /me/permissions,
// see src/contexts/PermissionContext.jsx), which every consumer must call
// usePermissions() to obtain and pass in. roleAllowsAction() below is the
// OLD, localStorage.menuPermission-based gate — kept only for isActionValueAllowed's
// row-level (record.actionPermission.<key>) use, which is a separate,
// per-record mechanism unrelated to role permissions.

export function isActionValueAllowed(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === 'object') {
    return isActionValueAllowed(value.permission ?? value.allowed ?? value.value);
  }
  return value !== false && value !== 0 && value !== '0';
}

// permission: "<module>.<key>" string from admin config (RowAction.permission,
// field.visiblePermission/editablePermission). can: the `can` function returned
// by usePermissions(). Falls back to the field/action's own moduleName when the
// permission string has no module prefix (a bare key, e.g. "edit").
export function checkPermission(can, permission, moduleName) {
  if (!permission) return true;
  if (typeof can !== 'function') return true;
  const [configuredModule, configuredKey] = String(permission).split('.');
  const module = configuredKey ? configuredModule : (moduleName || configuredModule);
  const key = configuredKey ?? configuredModule;
  return can(module, key);
}

// Deprecated — localStorage.menuPermission-based gate, superseded by
// checkPermission()/usePermissions(). No remaining call sites; kept only so a
// stray import doesn't break until it's confirmed unused everywhere.
export function roleAllowsAction(permission, moduleName) {
  if (!permission) return true;
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const [configuredModule, configuredKey] = String(permission).split('.');
    const moduleKey = configuredModule || String(moduleName || '').replace(/s$/i, '');
    const modulePermissions = permissions[moduleKey]
      ?? permissions[String(moduleName || '')]
      ?? permissions[String(moduleName || '').replace(/s$/i, '')]
      ?? {};
    return isActionValueAllowed(modulePermissions[configuredKey]);
  } catch {
    return true;
  }
}
